import React from 'react';
import { Container, Accordion, AccordionSummary, AccordionDetails, Typography, Grid, IconButton, Tabs, Tab } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDialog } from '../../hooks/dialog.hook';
import { HttpResponse } from '../../services/base.service';
import { Add, Delete, Edit, FitnessCenter, Visibility } from '@mui/icons-material';
import { DataGrid, GridActionsCellItem, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { AppToolbar } from '../../components/AppToolbar';
import { AppButton } from '../../components/AppButton';
import { ClassesServices } from '../../services/classes.service';
import { ClassesType } from '../../types/classes.type';
import { WorkoutType } from '../../types/workout.type';
import { UsersType } from '../../types/users.type';
import { useSession } from '../../hooks/auth.hook';

export type ClassSchedule = {
    id: number;
    start_time: string;
    trainer_name: string;
    workout_name: string;
};

export type ClassData = {
    id: number;
    date: string;
    workout_name?: string;
    schedules: ClassSchedule[];
};

export function ClassesList() {
    const [data, setData] = React.useState<ClassesType[]>([]);
    const navigate = useNavigate();
    const { showConfirmation } = useDialog();
    const { user } = useSession();
    const location = useLocation();
    const [selectedStudioId, setSelectedStudioId] = React.useState<number | null>();

    // When the component mounts, read the studio_id from the URL if present
    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const studioIdValue = params.get('studio_id') ?? user?.user_studios[0].studio_id;
        if (studioIdValue && Number(studioIdValue) !== selectedStudioId) {
            setSelectedStudioId(Number(studioIdValue));
            navigate(`?studio_id=${studioIdValue}`);
        }
    }, [location.search, user]);

    React.useEffect(() => {
        ClassesServices.getAll().then((response: HttpResponse<ClassesType[]>) => {
            setData(response.data);
        });
    }, [])

    const handleAdd = () => {
        navigate(`create?studio_id=${selectedStudioId}`);
    };

    const editClass = (date: string, studioId: number) => {
        navigate(`edit/?date=${date}&studio_id=${studioId}`);
    };

    const previewClass = (instanceId: number) => {
        navigate(`/classes/preview?instance_id=${instanceId}`);
    }

    const editExercises = (instanceId: string) => {
        navigate(`/classes/exercises/edit?instance_id=${instanceId}`);
    }

    const deleteDayClasses = (date: string, studioId: number) => {
        showConfirmation(`Are you sure you want to delete the ${date} class?`, () => {
            const deleteData = {
                date: date,
                studio_id: studioId
            }
            ClassesServices.deleteDayClasses(deleteData).then((response: HttpResponse) => {
                setData(data.filter((c) => c.date !== date));
            });
        });
    };

    const deleteSchedule = (id: number, schedule: string, workout: string) => {
        showConfirmation(`Are you sure you want to delete the ${workout} class schedule to ${schedule}?`, () => {
            ClassesServices.deleteScheduleById(id).then((response: HttpResponse) => {
                setData((prevData) =>
                    prevData.map((c) => ({
                        ...c,
                        instances: c.instances?.filter((s) => s.id !== id) || []
                    }))
                );
            });
        });
    };

    const scheduleColumns: GridColDef[] = [
        { field: 'start_time', headerName: 'Start Time', flex: 0.3 },
        {
            field: 'workout', headerName: 'Workout', flex: 1,
            renderCell: (params: GridRenderCellParams<any, WorkoutType>) => <span>{params.value?.name}</span>
        },
        {
            field: 'trainer', headerName: 'Trainer', flex: 1,
            renderCell: (params: GridRenderCellParams<any, UsersType>) => <span>{params.value?.name}</span>
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 0.3,
            sortable: false,
            renderCell: (params) => (
                <>
                    <GridActionsCellItem
                        icon={<Visibility />}
                        label="Preview"
                        onClick={() => previewClass(params.row.id)}
                    />
                    <GridActionsCellItem
                        icon={<FitnessCenter />}
                        label="Edit"
                        onClick={() => editExercises(params.row.id)}
                    />
                    <GridActionsCellItem
                        icon={<Delete />}
                        label="Delete"
                        onClick={() => deleteSchedule(params.row.id, params.row.start_time, params.row.workout.name)}
                    />
                </>
            ),
        },
    ];

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
        return date.toLocaleDateString('en-US', options);
    };

    const handleTabChange = (newValue: number) => {
        setSelectedStudioId(newValue);
        navigate(`?studio_id=${newValue}`);
    };


    return (
        <Container>
            <AppToolbar title='Classes'>
                <AppButton
                    startIcon={<Add />}
                    onClick={handleAdd}
                >
                    Add Class
                </AppButton>
            </AppToolbar>
            {/* Tabs header */}
            <Tabs
                value={selectedStudioId}
                onChange={(_, studioId) => handleTabChange(studioId)}
                variant="scrollable"
                scrollButtons="auto"
            >
                {user?.user_studios.map((userStudio, index) => (
                    <Tab
                        key={userStudio.studio_id}
                        label={userStudio.studio.name}
                        value={userStudio.studio_id} />
                ))}
            </Tabs>

            {/* Tab panels */}
            {user?.user_studios.map((userStudio) => (
                <div key={userStudio.studio_id} hidden={selectedStudioId !== userStudio.studio_id}>

                    {data
                        //Filter only classes that have instances for this studio
                        .filter((classData) =>
                            classData.instances?.some((instance) => instance.studio_id === userStudio.studio_id)
                        )
                        .map((classData) => {
                            const date = new Date(classData.date);
                            const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });
                            const fullDate = formatDate(classData.date);
                            return (
                                <Accordion key={`studio_${userStudio.studio_id} - ${classData.date} `} expanded>
                                    <AccordionSummary>
                                        <Grid container alignItems="center" justifyContent="space-between">
                                            <Grid item>
                                                <Typography component="span" style={{ color: '#1976d2', marginRight: 8 }}>
                                                    {dayOfWeek}
                                                </Typography>
                                                <Typography component="span" style={{ fontSize: 12 }}>
                                                    {fullDate}
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <IconButton onClick={() => editClass(classData.date, userStudio.studio_id)}>
                                                    <Edit />
                                                </IconButton>
                                                <IconButton onClick={() => deleteDayClasses(classData.date, userStudio.studio_id)}>
                                                    <Delete />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <DataGrid
                                            autoHeight
                                            /* Filtering only instances from the specific studio! */
                                            rows={classData.instances?.filter(i => i.studio_id === userStudio.studio_id)?.map((s) => ({ ...s, date: classData.date }))}
                                            columns={scheduleColumns}
                                            disableColumnMenu
                                            hideFooter
                                            columnHeaderHeight={0}
                                            getRowClassName={(params) =>
                                                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                                            }
                                            sx={{

                                                '& .MuiDataGrid-row:nth-of-type(odd)': {
                                                    backgroundColor: 'rgba(235, 235, 235, .7)',
                                                },
                                                '& .MuiDataGrid-row:nth-of-type(even)': {
                                                    backgroundColor: 'white',
                                                }
                                            }}
                                        />
                                    </AccordionDetails>
                                </Accordion>
                            )
                        })}
                </div>
            ))
            }
        </Container>
    );
};